import type { BookmarkChange, BookmarkRow, BrowserKind, DeviceExtension, DeviceRecord, InstalledExtension, NodeKind } from '@bsync/shared';

/** Row shape returned by PostgREST / push_changes (snake_case). */
export interface DbBookmark {
  id: string;
  kind: NodeKind;
  parent_id: string;
  position: string;
  title: string;
  url: string | null;
  content_hlc: string;
  location_hlc: string;
  delete_flag: boolean;
  deleted_hlc: string;
  deleted: boolean;
  rev: number | string;
  updated_at: string;
  updated_by: string | null;
}

export const BOOKMARK_COLUMNS =
  'id,kind,parent_id,position,title,url,content_hlc,location_hlc,delete_flag,deleted_hlc,deleted,rev,updated_at,updated_by';

export function bookmarkFromDb(r: DbBookmark): BookmarkRow {
  return {
    id: r.id,
    kind: r.kind,
    parentId: r.parent_id,
    position: r.position,
    title: r.title,
    url: r.url,
    contentHlc: r.content_hlc,
    locationHlc: r.location_hlc,
    deleteFlag: r.delete_flag,
    deletedHlc: r.deleted_hlc,
    deleted: r.deleted,
    rev: Number(r.rev),
    updatedAt: r.updated_at,
    updatedBy: r.updated_by,
  };
}

/** Flatten a change into the push_changes payload format. */
export function changeToDb(c: BookmarkChange): Record<string, unknown> {
  const out: Record<string, unknown> = { id: c.id };
  if (c.kind) out.kind = c.kind;
  if (c.content) {
    out.title = c.content.title;
    out.url = c.content.url;
    out.content_hlc = c.content.hlc;
  }
  if (c.location) {
    out.parent_id = c.location.parentId;
    out.position = c.location.position;
    out.location_hlc = c.location.hlc;
  }
  if (c.deletion) {
    out.delete_flag = c.deletion.deleted;
    out.deleted_hlc = c.deletion.hlc;
  }
  return out;
}

export interface DbDevice {
  id: string;
  name: string;
  browser: BrowserKind;
  platform: string | null;
  user_agent: string | null;
  app_version: string | null;
  created_at: string;
  last_seen_at: string | null;
  last_sync_at: string | null;
}

export function deviceFromDb(d: DbDevice): DeviceRecord {
  return {
    id: d.id,
    name: d.name,
    browser: d.browser,
    platform: d.platform,
    userAgent: d.user_agent,
    appVersion: d.app_version,
    createdAt: d.created_at,
    lastSeenAt: d.last_seen_at,
    lastSyncAt: d.last_sync_at,
  };
}

export interface DbDeviceExtension {
  device_id: string;
  ext_id: string;
  name: string;
  version: string;
  enabled: boolean;
  type: string;
  install_type: string;
  homepage_url: string | null;
  description: string | null;
  updated_at: string;
}

export function deviceExtensionFromDb(e: DbDeviceExtension): DeviceExtension {
  return {
    deviceId: e.device_id,
    extId: e.ext_id,
    name: e.name,
    version: e.version,
    enabled: e.enabled,
    type: e.type as DeviceExtension['type'],
    installType: e.install_type,
    homepageUrl: e.homepage_url,
    description: e.description,
    updatedAt: e.updated_at,
  };
}

export function extensionToDb(e: InstalledExtension): Record<string, unknown> {
  return {
    ext_id: e.extId,
    name: e.name,
    version: e.version,
    enabled: e.enabled,
    type: e.type,
    install_type: e.installType,
    homepage_url: e.homepageUrl,
    description: e.description,
  };
}
